import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  Icon,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";

// ─── MOCK DATA ───────────────────────────────────────────────────────────────
const MONTHLY_DATA = [
  {
    month: "Dec 2024",
    orders: 856,
    value: 16.87,
    noOfItems: 13875,
    matched: 85.3,
  },
  {
    month: "Jan 2025",
    orders: 892,
    value: 17.98,
    noOfItems: 14458,
    matched: 87.9,
  },
  {
    month: "Feb 2025",
    orders: 989,
    value: 19.87,
    noOfItems: 16031,
    matched: 88.4,
  },
  {
    month: "Mar 2025",
    orders: 1167,
    value: 21.06,
    noOfItems: 18916,
    matched: 91.2,
  },
  {
    month: "Apr 2025",
    orders: 1052,
    value: 23.49,
    noOfItems: 17052,
    matched: 89.7,
  },
  {
    month: "May 2025",
    orders: 1248,
    value: 28.75,
    noOfItems: 20229,
    matched: 92.6,
  },
];

const CUSTOMER_DATA = [
  {
    name: "Arvind Fashions Ltd",
    orders: 342,
    value: 8.42,
    noOfItems: 5540,
    matched: 93.7,
  },
  {
    name: "Raymond Ltd",
    orders: 298,
    value: 7.18,
    noOfItems: 4830,
    matched: 90.2,
  },
  {
    name: "Welspun India",
    orders: 256,
    value: 5.64,
    noOfItems: 4150,
    matched: 89.4,
  },
  {
    name: "Trident Group",
    orders: 187,
    value: 4.12,
    noOfItems: 3030,
    matched: 91.8,
  },
  {
    name: "Donear Industries",
    orders: 165,
    value: 3.39,
    noOfItems: 2675,
    matched: 88.6,
  },
];

const ITEM_DATA = [
  {
    name: "Cotton Shirting Fabric",
    total: 6842,
    noOfItems: 6842,
    matched: 93.2,
  },
  { name: "Polyester Fabric", total: 4128, noOfItems: 4128, matched: 89.4 },
  { name: "Denim Fabric", total: 3847, noOfItems: 3847, matched: 91.8 },
  { name: "Viscose Fabric", total: 2987, noOfItems: 2987, matched: 88.6 },
  { name: "Blended Fabric", total: 2156, noOfItems: 2156, matched: 90.1 },
];

const SIZE_DATA = [
  { name: "S", total: 3412, noOfItems: 3412, matched: 92.4 },
  { name: "M", total: 4382, noOfItems: 4382, matched: 90.3 },
  { name: "L", total: 3847, noOfItems: 3847, matched: 91.6 },
  { name: "XL", total: 1876, noOfItems: 1876, matched: 88.7 },
  { name: "XXL", total: 1370, noOfItems: 1370, matched: 86.8 },
];

const LATEST = MONTHLY_DATA[MONTHLY_DATA.length - 1];
const TOTAL_ITEMS = 20229;
const ITEMS_MATCHED = 18742;
const ITEMS_MISMATCHED = 1487;
const MATCH_ACCURACY = 92.6;

const TAB_CONFIGS = [
  {
    label: "Monthly View",
    linkLabel: "View full report",
    columns: [
      { key: "name", header: "Month" },
      { key: "orders", header: "Orders" },
      { key: "noOfItems", header: "Items #" },
      //   { key: "matched", header: "Matched %" },
    ],
    rows: MONTHLY_DATA.map((m) => ({
      name: m.month,
      orders: m.orders,
      value: m.noOfItems,
      noOfItems: m.noOfItems,
      matched: m.matched,
    })),
  },
  {
    label: "Customer View",
    linkLabel: "View all customers",
    columns: [
      { key: "name", header: "Customer" },
      { key: "orders", header: "Orders" },
      { key: "noOfItems", header: "Items #" },
      //   { key: "matched", header: "Matched %" },
    ],
    rows: CUSTOMER_DATA,
  },
  {
    label: "Item Wise View",
    linkLabel: "View all items",
    columns: [
      { key: "name", header: "Item" },
      { key: "total", header: "Total Items" },
      //   { key: "matched", header: "Matched %" },
    ],
    rows: ITEM_DATA,
  },
  {
    label: "Size Wise View",
    linkLabel: "View all sizes",
    columns: [
      { key: "name", header: "Size" },
      { key: "total", header: "Total Items" },
      //   { key: "matched", header: "Matched %" },
    ],
    rows: SIZE_DATA,
  },
];

const matchedColor = (val, theme) => {
  if (val >= 90) return theme.palette.success.main;
  if (val >= 80) return theme.palette.warning.main;
  return theme.palette.error.main;
};

// ─── SHARED COMPONENTS ───────────────────────────────────────────────────────
function SectionHeader({ title, subtitle, action }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      mb={1}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" fontSize={11}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <Box ml={1} flexShrink={0}>
          {action}
        </Box>
      )}
    </Stack>
  );
}

function KpiCard({ title, value, subtitle, icon, color, trend }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${alpha(color, 0.25)}`,
        borderRadius: 1,
        height: "100%",
        background: `linear-gradient(135deg, ${alpha(color, 0.07)} 0%, ${
          theme.palette.background.paper
        } 100%)`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 24px ${alpha(color, 0.2)}`,
        },
      }}
    >
      <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1} minWidth={0}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.4}
              fontSize={9.5}
              display="block"
              noWrap
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={800}
              color={color}
              mt={0.2}
              lineHeight={1.1}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 0.75,
              flexShrink: 0,
              ml: 1,
              bgcolor: alpha(color, 0.13),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color, fontSize: 16 }}>{icon}</Icon>
          </Box>
        </Stack>
        {trend ? (
          <Stack direction="row" alignItems="center" spacing={0.3} mt={0.5}>
            <Icon sx={{ fontSize: 12, color: "success.main" }}>
              trending_up
            </Icon>
            <Typography
              variant="caption"
              fontWeight={700}
              color="success.main"
              fontSize={10.5}
            >
              {trend}
            </Typography>
            <Typography variant="caption" color="text.disabled" fontSize={10.5}>
              vs Apr
            </Typography>
          </Stack>
        ) : (
          subtitle && (
            <Typography
              variant="caption"
              fontWeight={700}
              color={color}
              mt={0.5}
              display="block"
              fontSize={10.5}
            >
              {subtitle}
            </Typography>
          )
        )}
      </CardContent>
    </Card>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function PoDashboard() {
  const theme = useTheme();
  const [month, setMonth] = useState(LATEST.month);

  const chartBase = {
    toolbar: { show: false },
    background: "transparent",
    fontFamily: theme.typography.fontFamily,
  };
  const labelStyle = {
    style: { fontSize: "11px", colors: theme.palette.text.secondary },
  };

  const trendOptions = useMemo(
    () => ({
      chart: { ...chartBase, type: "line" },
      colors: ["#7C4DFF", "#00C853"],
      stroke: { width: [0, 3], curve: "smooth" },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 2 } },
      xaxis: {
        categories: MONTHLY_DATA.map((m) => m.month),
        labels: labelStyle,
      },
      yaxis: [
        {
          title: { text: "No. of Orders", style: { fontSize: "11px" } },
          labels: labelStyle,
        },
        {
          opposite: true,
          title: {
            text: "Order Value (₹ Cr)",
            style: { fontSize: "11px" },
          },
          labels: labelStyle,
        },
      ],
      legend: { position: "top", horizontalAlign: "left", fontSize: "12px" },
      grid: {
        borderColor: alpha(theme.palette.divider, 0.6),
        strokeDashArray: 4,
      },
      tooltip: { theme: theme.palette.mode, shared: true, intersect: false },
      dataLabels: { enabled: false },
    }),
    [theme],
  );
  const trendSeries = [
    {
      name: "No. of Orders",
      type: "column",
      data: MONTHLY_DATA.map((m) => m.orders),
    },
    {
      name: "Order Value (₹ Cr)",
      type: "line",
      data: MONTHLY_DATA.map((m) => m.value),
    },
  ];

  const donutOptions = useMemo(
    () => ({
      chart: { ...chartBase, type: "donut" },
      colors: ["#00C853", "#F44336"],
      labels: ["Matched Automatically", "Mismatches"],
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total Items",
                height: 10,
                itemMargin: { vertical: 1 },
                fontSize: "12px",
                marginBottom: 2,
                fontWeight: 500,
                color: theme.palette.text.primary,
                formatter: () => TOTAL_ITEMS.toLocaleString("en-IN"),
              },
            },
          },
        },
      },
      legend: { position: "bottom", fontSize: "10px" },
      dataLabels: { enabled: false, formatter: (v) => `${v.toFixed(1)}%` },
      tooltip: { theme: theme.palette.mode },
    }),
    [theme],
  );
  const donutSeries = [ITEMS_MATCHED, ITEMS_MISMATCHED];

  const sparklineOptions = useMemo(
    () => ({
      chart: { ...chartBase, type: "area", sparkline: { enabled: true } },
      colors: ["#00C853"],
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: { opacityFrom: 0.4, opacityTo: 0.05 },
      },
      tooltip: { enabled: false },
    }),
    [theme],
  );
  const sparklineSeries = [
    { name: "Match Accuracy", data: MONTHLY_DATA.map((m) => m.matched) },
  ];

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: { xs: 1.5, md: 2 },
      }}
    >
      {/* ── HEADER ───────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        mb={1.5}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} letterSpacing={-0.5}>
            Purchase Order Dashboard
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize={11}>
            Order intake, value trends and item matching accuracy
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small">
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{
                fontSize: 12,
                minWidth: 120,
                "& .MuiSelect-select": { py: 0.6 },
              }}
            >
              {MONTHLY_DATA.map((m) => (
                <MenuItem key={m.month} value={m.month} sx={{ fontSize: 13 }}>
                  {m.month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon fontSize="small">filter_alt</Icon>}
            sx={{ textTransform: "none" }}
          >
            Filters
          </Button>
        </Stack>
      </Stack>

      {/* ── KPI CARDS ────────────────────────────────────────────────────── */}
      <Grid container spacing={1} mb={1.5}>
        {[
          {
            title: "No. of Orders Received",
            value: LATEST.orders.toLocaleString("en-IN"),
            icon: "shopping_cart",
            color: "#7C4DFF",
            trend: "+18.6%",
          },
          {
            title: "Order Value",
            value: `₹${LATEST.value.toFixed(2)} Cr`,
            icon: "payments",
            color: "#00BFA5",
            trend: "+22.4%",
          },
          {
            title: "Avg Order Size",
            value: "₹23.07 L",
            icon: "straighten",
            color: "#2979FF",
            trend: "+3.7%",
          },
          {
            title: "Items Auto Matched",
            value: ITEMS_MATCHED.toLocaleString("en-IN"),
            icon: "check_circle",
            color: "#43A047",
            subtitle: `${((ITEMS_MATCHED / TOTAL_ITEMS) * 100).toFixed(
              1,
            )}% of total items`,
          },
          {
            title: "Items Mismatches",
            value: ITEMS_MISMATCHED.toLocaleString("en-IN"),
            icon: "warning",
            color: "#E53935",
            subtitle: `${((ITEMS_MISMATCHED / TOTAL_ITEMS) * 100).toFixed(
              1,
            )}% of total items`,
          },
        ].map((k) => (
          <Grid item xs={12} sm={6} md={2.4} key={k.title}>
            <KpiCard {...k} />
          </Grid>
        ))}
      </Grid>

      {/* ── ORDERS TREND + MATCHING OVERVIEW ────────────────────────────── */}
      <Grid container spacing={1.5} mb={1.5}>
        <Grid item xs={12} md={7}>
          <Card
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              height: "100%",
            }}
          >
            <SectionHeader
              title="Orders Trend (Monthly)"
              subtitle="No. of Orders vs Order Value"
            />
            <ReactApexChart
              options={trendOptions}
              series={trendSeries}
              type="line"
              height={190}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              height: "100%",
            }}
          >
            <SectionHeader title="Matching Overview" />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              mt={1}
            >
              <ReactApexChart
                options={donutOptions}
                series={donutSeries}
                type="donut"
                height={220}
              />
              <Stack alignItems="center" mt={0.5}>
                <Typography variant="caption" style={{ fontWeight: 700 }}>
                  Match Accuracy
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    color="success.main"
                  >
                    {MATCH_ACCURACY}%
                  </Typography>
                </Stack>
                <Box width={120}>
                  <ReactApexChart
                    options={sparklineOptions}
                    series={sparklineSeries}
                    type="area"
                    height={36}
                    width={120}
                  />
                </Box>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* ── DETAIL TABLES SIDE BY SIDE ───────────────────────────────────── */}
      <Grid container spacing={1.5}>
        {TAB_CONFIGS.map((cfg) => (
          <Grid item xs={12} sm={6} md={3} key={cfg.label}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                height: "100%",
              }}
            >
              <Box px={1.5} pt={1}>
                <Typography variant="subtitle2" fontWeight={700} fontSize={12}>
                  {cfg.label}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          fontSize: 9.5,
                          textTransform: "uppercase",
                          letterSpacing: 0.3,
                          py: 0.4,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {cfg.columns.map((c, i) => (
                        <TableCell
                          style={i === 0 ? { minWidth: 130 } : {}}
                          key={c.key}
                        >
                          {c.header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cfg.rows.map((row, index) => (
                      <TableRow
                        key={`${cfg.label}-${index}`}
                        hover
                        sx={{ "& td": { py: 0.3 } }}
                      >
                        {cfg.columns.map((c, i) => (
                          <TableCell
                            style={i === 0 ? { minWidth: 130 } : {}}
                            key={c.key}
                          >
                            {c.key === "matched" ? (
                              <Chip
                                label={`${row.matched}%`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 9,
                                  height: 16,
                                  color: matchedColor(row.matched, theme),
                                  bgcolor: alpha(
                                    matchedColor(row.matched, theme),
                                    0.1,
                                  ),
                                }}
                              />
                            ) : c.key === "value" ? (
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="primary.main"
                                fontSize={10.5}
                              >
                                {row.value.toFixed(2)}
                              </Typography>
                            ) : c.key === "name" ? (
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                fontSize={10.5}
                              >
                                {row.name}
                              </Typography>
                            ) : (
                              <Typography variant="caption" fontSize={10.5}>
                                {row[c.key]?.toLocaleString("en-IN")}
                              </Typography>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box px={1.5} py={0.6}>
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={600}
                  fontSize={10}
                  sx={{ cursor: "pointer" }}
                >
                  {cfg.linkLabel} →
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
